import config from "appConfig";
import { getLinkForCourseLesson } from "../../../../services/Utils";

import lesson0402 from "../../course-04/lesson-02/lesson";
import lesson0403 from "../../course-04/lesson-03/lesson";
import lesson0404 from "../../course-04/lesson-04/lesson";

const linkLesson0402 = getLinkForCourseLesson("e0db4477-1995-4380-9c35-2338dcf5e504", lesson0402.id);
const linkLesson0403 = getLinkForCourseLesson("e0db4477-1995-4380-9c35-2338dcf5e504", lesson0403.id);
const linkLesson0404 = getLinkForCourseLesson("e0db4477-1995-4380-9c35-2338dcf5e504", lesson0404.id);

const theory = ` <h3 class=sectionHead>Introduction</h3>
<p>
   In <a href="${linkLesson0403}" target="_blank">Lesson 3</a> and <a href="${linkLesson0404}" target="_blank">Lesson 4</a>, we saw that, under certain conditions, the equations governing the behaviour of mechanical or acoustic systems have formal analogies with electrical equations. Thus, the behaviour of such systems can be modelled using lumped elements, <em><span class=cmti-10>i.e.</span></em> electrical elements that approximates the behaviour of mechanical or acoustical systems. In this Lesson, after presenting a synthesis of the basic analogies, their use on more complex systems (
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mo class=MathClass-rel>></mo>
   </math>
   1-DOF) will be introduced.
<h3 class=sectionHead>Summary of analogies</h3>
<p>
   Let us consider the following lumped mechanical elements: a mass
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>M</mi>
   </math>, an ideal spring of stiffness
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>K</mi>
      <mo class=MathClass-rel>=</mo>
      <mn>1</mn>
      <mo class=MathClass-bin>∕</mo>
      <mi>C</mi>
   </math>
   and a dashpot of mechanical resistance
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>R</mi>
   </math>.  <a href="#" data-img-src=${config.ASSETS_URL}/04/05/theory/figure-1.svg data-img-alt="Figure 1. Electrical analogies for mechanical systems.">Figure 1</a> shows the electrical analogies of these mechanical elements in the direct analogy (also known as the impedance analogy) that will be used in this course. Note that an indirect analogy or admittance analogy also exists, but it will not be used here.<br class=newline>
   <figure class=figure>
<p><img src=${config.ASSETS_URL}/04/05/theory/figure-1.svg alt="Figure 1. Electrical analogies for mechanical systems."><figcaption class=caption>Figure 1. Electrical analogies for mechanical systems.</figcaption></figure>
<p>
   <a href="#" data-img-src=${config.ASSETS_URL}/04/05/theory/figure-2.svg data-img-alt="Figure 2. Electrical analogies for acoustical systems.">Figure 2</a> shows the electrical analogies of the following acoustical elements: open tube, closed tube and narrow tube in the direct analogy. Note that for both mechanical and acoustical domains, there is a dissipative term, symbolised by a resistor in the electrical analogy, a kinetic energy storage term, symbolised by an inductor and a potential energy storage term, symbolised by the capacitance.
   <figure class=figure>
<p><img src=${config.ASSETS_URL}/04/05/theory/figure-2.svg alt="Figure 2. Electrical analogies for acoustical systems."><figcaption class=caption>Figure 2. Electrical analogies for acoustical systems.</figcaption></figure>
<h3 class=sectionHead>Examples of the use of analogies</h3>
<p>Let us now look at how to use these analogies to model the behaviour of acoustic and mechanical systems. The case of systems with one degree of freedom has already been covered in <a href="${linkLesson0403}" target="_blank">Lesson 3</a> and <a href="${linkLesson0404}" target="_blank">Lesson 4</a>, let us now look at the behaviour of more complex systems.
<h4 class=subsectionHead>Mechanical systems</h4>
<p>
   Consider the mechanical part of an accelerometer depicted in <a href="#" data-img-src=${config.ASSETS_URL}/04/05/theory/figure-3.svg data-img-alt="Figure 3.Mechanical network of an accelerometer.">Figure 3</a>. When rigidly attached to the object to be measured, the accelerometer case vibrates at the velocity
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>V</mi>
         </mrow>
         <mrow>
            <mi>c</mi>
         </mrow>
      </msub>
   </math>. This vibration is transmitted to the seismic mass
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>M</mi>
         </mrow>
         <mrow>
            <mi>s</mi>
         </mrow>
      </msub>
   </math>
   via the piezoelectric element of compliance
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>C</mi>
         </mrow>
         <mrow>
            <mi>p</mi>
         </mrow>
      </msub>
   </math>
   and mechanical resistance
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>R</mi>
         </mrow>
         <mrow>
            <mi>p</mi>
         </mrow>
      </msub>
   </math>. The seismic mass then vibrates at the velocity
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>V</mi>
         </mrow>
         <mrow>
            <mi>s</mi>
         </mrow>
      </msub>
   </math>.<br class=newline>
   <figure class=figure>
<p><img src=${config.ASSETS_URL}/04/05/theory/figure-3.svg alt="Figure 3.Mechanical network of an accelerometer."><figcaption class=caption>Figure 3.Mechanical network of an accelerometer.</figcaption></figure>
<p>Let us now try to draw the equivalent circuit electrical to this mechanical system:
<ul class=itemize1>
   <li class=itemize>
      let’s start by identifying the source term, which corresponds here to the velocity of the object to be measured that is transmitted to the accelerometer case. We will therefore use a current source of value
      <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
         <msub>
            <mrow>
               <mi>V</mi>
            </mrow>
            <mrow>
               <mi>c</mi>
            </mrow>
         </msub>
      </math>,
   <li class=itemize>
      now, let us identify the different masses (rigid objects) of the system and their respective velocities: here,
      <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
         <msub>
            <mrow>
               <mi>M</mi>
            </mrow>
            <mrow>
               <mi>c</mi>
            </mrow>
         </msub>
      </math>
      vibrating at
      <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
         <msub>
            <mrow>
               <mi>V</mi>
            </mrow>
            <mrow>
               <mi>c</mi>
            </mrow>
         </msub>
      </math>
      and
      <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
         <msub>
            <mrow>
               <mi>M</mi>
            </mrow>
            <mrow>
               <mi>s</mi>
            </mrow>
         </msub>
      </math>
      vibrating at
      <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
         <msub>
            <mrow>
               <mi>V</mi>
            </mrow>
            <mrow>
               <mi>s</mi>
            </mrow>
         </msub>
      </math>. These inductors will be placed in different branches,
   <li class=itemize>
      finally, let us look at the elements subjected to a difference in velocities:
      <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
         <msub>
            <mrow>
               <mi>C</mi>
            </mrow>
            <mrow>
               <mi>p</mi>
            </mrow>
         </msub>
      </math>
      and
      <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
         <msub>
            <mrow>
               <mi>R</mi>
            </mrow>
            <mrow>
               <mi>p</mi>
            </mrow>
         </msub>
      </math>, here subject to
      <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
         <msub>
            <mrow>
               <mi>V</mi>
            </mrow>
            <mrow>
               <mi>c</mi>
            </mrow>
         </msub>
         <mo class=MathClass-bin>−</mo>
         <msub>
            <mrow>
               <mi>V</mi>
            </mrow>
            <mrow>
               <mi>s</mi>
            </mrow>
         </msub>
      </math>. Their positioning in the circuit will be done in a way that respects Kirchhoff’s current law.
</ul>
<p>
   Using these considerations, it is possible to draw the equivalent electrical circuit shown in <a href="#" data-img-src=${config.ASSETS_URL}/04/05/theory/figure-4.svg data-img-alt="Figure 4. Electrical circuit equivalent to the mechanical part of an accelerometer.">Figure 4</a>, which can be used, for instance, to calculate the frequency response function
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>V</mi>
         </mrow>
         <mrow>
            <mi>s</mi>
         </mrow>
      </msub>
      <mo class=MathClass-bin>∕</mo>
      <msub>
         <mrow>
            <mi>V</mi>
         </mrow>
         <mrow>
            <mi>c</mi>
         </mrow>
      </msub>
   </math>.<br class=newline>
   <figure class=figure>
      <img src=${config.ASSETS_URL}/04/05/theory/figure-4.svg alt="Figure 4. Electrical circuit equivalent to the mechanical part of an accelerometer.">
      <figcaption class=caption>Figure 4. Electrical circuit equivalent to the mechanical part of an accelerometer.</figcaption>
   </figure>
<p>Let’s first use the current divider formula (see <a href="${linkLesson0402}" target="_blank">Lesson 2</a>)
<table class=equation>
   <tr>
      <td>
         <math xmlns=http://www.w3.org/1998/Math/MathML class=equation display=block>
            <msub>
               <mrow>
                  <mi>V</mi>
               </mrow>
               <mrow>
                  <mi>s</mi>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>R</mi>
                     </mrow>
                     <mrow>
                        <mi>p</mi>
                     </mrow>
                  </msub>
                  <mo class=MathClass-bin>+</mo>
                  <mfrac>
                     <mrow>
                        <mn>1</mn>
                     </mrow>
                     <mrow>
                        <mi mathvariant=italic>jω</mi>
                        <msub>
                           <mrow>
                              <mi>C</mi>
                           </mrow>
                           <mrow>
                              <mi>p</mi>
                           </mrow>
                        </msub>
                     </mrow>
                  </mfrac>
               </mrow>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>R</mi>
                     </mrow>
                     <mrow>
                        <mi>p</mi>
                     </mrow>
                  </msub>
                  <mo class=MathClass-bin>+</mo>
                  <mfrac>
                     <mrow>
                        <mn>1</mn>
                     </mrow>
                     <mrow>
                        <mi mathvariant=italic>jω</mi>
                        <msub>
                           <mrow>
                              <mi>C</mi>
                           </mrow>
                           <mrow>
                              <mi>p</mi>
                           </mrow>
                        </msub>
                     </mrow>
                  </mfrac>
                  <mo class=MathClass-bin>+</mo>
                  <mi mathvariant=italic>jω</mi>
                  <msub>
                     <mrow>
                        <mi>M</mi>
                     </mrow>
                     <mrow>
                        <mi>s</mi>
                     </mrow>
                  </msub>
               </mrow>
            </mfrac>
            <msub>
               <mrow>
                  <mi>V</mi>
               </mrow>
               <mrow>
                  <mi>c</mi>
               </mrow>
            </msub>
            <mo class=MathClass-punc></mo>
         </math>
      <td class=eq-no>(1)
</table>
<p>which can be rewritten as
<table class=equation>
   <tr>
      <td>
         <math xmlns=http://www.w3.org/1998/Math/MathML class=equation display=block>
            <mfrac>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>V</mi>
                     </mrow>
                     <mrow>
                        <mi>s</mi>
                     </mrow>
                  </msub>
               </mrow>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>V</mi>
                     </mrow>
                     <mrow>
                        <mi>c</mi>
                     </mrow>
                  </msub>
               </mrow>
            </mfrac>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <mn>1</mn>
                  <mo class=MathClass-bin>+</mo>
                  <mi mathvariant=italic>jω</mi>
                  <msub>
                     <mrow>
                        <mi>C</mi>
                     </mrow>
                     <mrow>
                        <mi>p</mi>
                     </mrow>
                  </msub>
                  <msub>
                     <mrow>
                        <mi>R</mi>
                     </mrow>
                     <mrow>
                        <mi>p</mi>
                     </mrow>
                  </msub>
               </mrow>
               <mrow>
                  <mn>1</mn>
                  <mo class=MathClass-bin>+</mo>
                  <mi mathvariant=italic>jω</mi>
                  <msub>
                     <mrow>
                        <mi>C</mi>
                     </mrow>
                     <mrow>
                        <mi>p</mi>
                     </mrow>
                  </msub>
                  <msub>
                     <mrow>
                        <mi>R</mi>
                     </mrow>
                     <mrow>
                        <mi>p</mi>
                     </mrow>
                  </msub>
                  <mo class=MathClass-bin>−</mo>
                  <msup>
                     <mrow>
                        <mi>ω</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msup>
                  <msub>
                     <mrow>
                        <mi>M</mi>
                     </mrow>
                     <mrow>
                        <mi>s</mi>
                     </mrow>
                  </msub>
                  <msub>
                     <mrow>
                        <mi>C</mi>
                     </mrow>
                     <mrow>
                        <mi>p</mi>
                     </mrow>
                  </msub>
               </mrow>
            </mfrac>
            <mo class=MathClass-punc></mo>
         </math>
      <td class=eq-no>(2)
</table>
<p>
   The latter is plotted in <a href="#" data-img-src=${config.ASSETS_URL}/04/05/theory/figure-5.svg data-img-alt="Figure 5. Frequency response <math xmlns=http://www.w3.org/1998/Math/MathML display=inline><msub><mrow><mi>V</mi></mrow><mrow><mi>s</mi></mrow></msub><mo class=MathClass-bin>∕</mo><msub><mrow><mi>V</mi></mrow><mrow><mi>c</mi></mrow></msub></math> of the mechanical part of an accelerometer.">Figure 5</a> for a typical accelerometer. The frequency response is flat below the resonance frequency of the system “moving mass - compliance of the piezoelectric element”. The conversion from movement to an electrical signal is done via the piezoelectric effect of the material, giving an output proportional to the normal acceleration of the sensor.
   <figure class=figure>
<p><img src=${config.ASSETS_URL}/04/05/theory/figure-5.svg alt="Figure 5. Frequency response <math xmlns=http://www.w3.org/1998/Math/MathML display=inline><msub><mrow><mi>V</mi></mrow><mrow><mi>s</mi></mrow></msub><mo class=MathClass-bin>∕</mo><msub><mrow><mi>V</mi></mrow><mrow><mi>c</mi></mrow></msub></math> of the mechanical part of an accelerometer."><figcaption class=caption>Figure 5. Frequency response <math xmlns=http://www.w3.org/1998/Math/MathML display=inline><msub><mrow><mi>V</mi></mrow><mrow><mi>s</mi></mrow></msub><mo class=MathClass-bin>∕</mo><msub><mrow><mi>V</mi></mrow><mrow><mi>c</mi></mrow></msub></math> of the mechanical part of an accelerometer.</figcaption></figure>
<h4 class=subsectionHead>Acoustical systems</h4>
<p>
   In this section, it will be explained how to draw the electrical circuit equivalent to an acoustical system. Let us consider the acoustical network represented in <a href="#" data-img-src=${config.ASSETS_URL}/04/05/theory/figure-6.svg data-img-alt="Figure 6. Acoustical network.">Figure 6</a>. It consists of four tubes, whose dimensions are defined either by a length and a section for an open tube or by a volume for a closed tube. Indices 1 to 4 are used to distinguish them.
   <figure class=figure>
<p><img src=${config.ASSETS_URL}/04/05/theory/figure-6.svg alt="Figure 6. Acoustical network."><figcaption class=caption>Figure 6. Acoustical network.</figcaption></figure>
<p>Before drawing its equivalent electrical circuit we must answer the following questions:
<ul class=itemize1>
   <li class=itemize>
      <strong><span class=cmbx-10>what is the validity of the lumped elements used for electro-acoustic </span><span class=cmbx-10>analogies?</span></strong> The equivalent circuit using lumped elements is valid in the low frequency range,<em> <span class=cmti-10>i.e.</span></em> as long as the dimensions of the involved acoustical systems are much smaller than the wavelength:
      <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
         <msub>
            <mrow>
               <mi>l</mi>
            </mrow>
            <mrow>
               <mi>i</mi>
            </mrow>
         </msub>
      </math>
      ,
      <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
         <msqrt>
            <mrow>
               <msub>
                  <mrow>
                     <mi>S</mi>
                  </mrow>
                  <mrow>
                     <mi>i</mi>
                  </mrow>
               </msub>
            </mrow>
         </msqrt>
      </math>
      ,
      <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
         <mroot>
            <mrow>
               <msub>
                  <mrow>
                     <mi>V</mi>
                  </mrow>
                  <mrow>
                     <mi>i</mi>
                  </mrow>
               </msub>
            </mrow>
            <mrow>
               <mn>3</mn>
            </mrow>
         </mroot>
         <mo class=MathClass-rel>≪</mo>
         <mi>λ</mi>
      </math>. An acceptable ratio is
      <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
         <mn>2</mn>
         <mi>π</mi>
      </math>
      times the wavelength.
   <li class=itemize>
      <strong><span class=cmbx-10>how to deal with sudden changes of section area?</span></strong> On either side of the interface between two tubes of section
      <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
         <msub>
            <mrow>
               <mi>S</mi>
            </mrow>
            <mrow>
               <mn>1</mn>
            </mrow>
         </msub>
      </math>
      and
      <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
         <msub>
            <mrow>
               <mi>S</mi>
            </mrow>
            <mrow>
               <mn>2</mn>
            </mrow>
         </msub>
      </math>, (see <a href="#" data-img-src=${config.ASSETS_URL}/04/05/theory/figure-7.svg data-img-alt="Figure 7. Section change in a waveguide.">Figure 7</a>), the pressure and velocity are continuous:
      <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
         <msub>
            <mrow>
               <mi>p</mi>
            </mrow>
            <mrow>
               <mn>1</mn>
            </mrow>
         </msub>
         <mo class=MathClass-rel>=</mo>
         <msub>
            <mrow>
               <mi>p</mi>
            </mrow>
            <mrow>
               <mn>2</mn>
            </mrow>
         </msub>
      </math>
      and
      <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
         <msub>
            <mrow>
               <mi>V</mi>
            </mrow>
            <mrow>
               <mn>1</mn>
            </mrow>
         </msub>
         <mo class=MathClass-rel>=</mo>
         <msub>
            <mrow>
               <mi>V</mi>
            </mrow>
            <mrow>
               <mn>2</mn>
            </mrow>
         </msub>
      </math>. Note that the velocity is null on the light blue part of the guide because it is rigid. In the area highlighted in yellow, there is a rapid expansion of the velocity field, due to section change from
      <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
         <msub>
            <mrow>
               <mi>S</mi>
            </mrow>
            <mrow>
               <mn>1</mn>
            </mrow>
         </msub>
      </math>
      to
      <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
         <msub>
            <mrow>
               <mi>S</mi>
            </mrow>
            <mrow>
               <mn>2</mn>
            </mrow>
         </msub>
      </math>. Downstream of this region, the velocity becomes constant over the entire section of the tube (as
      <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
         <msqrt>
            <mrow>
               <msub>
                  <mrow>
                     <mi>S</mi>
                  </mrow>
                  <mrow>
                     <mn>1</mn>
                  </mrow>
               </msub>
            </mrow>
         </msqrt>
      </math>
      ,
      <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
         <msqrt>
            <mrow>
               <msub>
                  <mrow>
                     <mi>S</mi>
                  </mrow>
                  <mrow>
                     <mn>2</mn>
                  </mrow>
               </msub>
            </mrow>
         </msqrt>
         <mo class=MathClass-rel>≪</mo>
         <mi>λ</mi>
      </math>). The velocity values are governed by the law of conservation of flow rates:
      <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
         <msub>
            <mrow>
               <mi>Q</mi>
            </mrow>
            <mrow>
               <mn>1</mn>
            </mrow>
         </msub>
         <mo class=MathClass-rel>=</mo>
         <msub>
            <mrow>
               <mi>S</mi>
            </mrow>
            <mrow>
               <mn>1</mn>
            </mrow>
         </msub>
         <msub>
            <mrow>
               <mi>V</mi>
            </mrow>
            <mrow>
               <mn>1</mn>
            </mrow>
         </msub>
         <mo class=MathClass-rel>=</mo>
         <msub>
            <mrow>
               <mi>S</mi>
            </mrow>
            <mrow>
               <mn>2</mn>
            </mrow>
         </msub>
         <msub>
            <mrow>
               <mi>V</mi>
            </mrow>
            <mrow>
               <mn>2</mn>
            </mrow>
         </msub>
         <mo class=MathClass-rel>=</mo>
         <msub>
            <mrow>
               <mi>Q</mi>
            </mrow>
            <mrow>
               <mn>2</mn>
            </mrow>
         </msub>
      </math>. Please note that the yellow zone will be taken into account in the length correction of the left tube (see <a href="${linkLesson0404}" target="_blank">Lesson 4</a>). In summary, at the interface between two waveguides, pressure and flow rate are conserved (
      <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
         <msub>
            <mrow>
               <mi>p</mi>
            </mrow>
            <mrow>
               <mn>1</mn>
            </mrow>
         </msub>
         <mo class=MathClass-rel>=</mo>
         <msub>
            <mrow>
               <mi>p</mi>
            </mrow>
            <mrow>
               <mn>2</mn>
            </mrow>
         </msub>
      </math>
      and
      <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
         <msub>
            <mrow>
               <mi>Q</mi>
            </mrow>
            <mrow>
               <mn>1</mn>
            </mrow>
         </msub>
         <mo class=MathClass-rel>=</mo>
         <msub>
            <mrow>
               <mi>Q</mi>
            </mrow>
            <mrow>
               <mn>2</mn>
            </mrow>
         </msub>
      </math>), which is why these two quantities are chosen when modelling the behaviour of such systems. For a junction between more than two tubes, the conservation of flow rates always applies in a similar way to Kirchhoff’s current law for electrical circuits.
</ul>
<figure class=figure>
   <img src=${config.ASSETS_URL}/04/05/theory/figure-7.svg alt="Figure 7. Section change in a waveguide.">
   <figcaption class=caption>Figure 7. Section change in a waveguide.</figcaption>
</figure>
<p>Let’s go back to the system shown in <a href="#" data-img-src=${config.ASSETS_URL}/04/05/theory/figure-6.svg data-img-alt="Figure 6. Acoustical network.">Figure 6</a> and apply the following method to get its equivalent circuit:
<ol class=enumerate1>
   <li id=x1-5006x1 class=enumerate>
      identify the source term, here the pressure
      <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
         <msub>
            <mrow>
               <mi>p</mi>
            </mrow>
            <mrow>
               <mn>1</mn>
            </mrow>
         </msub>
      </math>
      , created by the loudspeaker and exiting the system,
   <li id=x1-5008x2 class=enumerate>find all interfaces between acoustic elements,
   <li id=x1-5010x3 class=enumerate>identify the predominant behaviour of each element (is it more closed or more open?). Here, the yellow waveguides are fully open and will be described by inductors while the light blue waveguides are almost closed and will be described by capacitors,
   <li id=x1-5012x4 class=enumerate>
      use conservation laws at each interface. For example, the volume flow rate at the right side of the tube
      <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
         <mn>1</mn>
      </math>
      will split into two parts: one that goes into the volume of the second tube, the other flowing into the third tube:
      <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
         <msub>
            <mrow>
               <mi>Q</mi>
            </mrow>
            <mrow>
               <mn>1</mn>
            </mrow>
         </msub>
         <mo class=MathClass-rel>=</mo>
         <msub>
            <mrow>
               <mi>Q</mi>
            </mrow>
            <mrow>
               <mn>2</mn>
            </mrow>
         </msub>
         <mo class=MathClass-bin>+</mo>
         <msub>
            <mrow>
               <mi>Q</mi>
            </mrow>
            <mrow>
               <mn>3</mn>
            </mrow>
         </msub>
      </math>.
</ol>
<p>
   Using these above considerations, it is possible to draw the circuit represented in <a href="#" data-img-src=${config.ASSETS_URL}/04/05/theory/figure-8.svg data-img-alt="Figure 8.Electrical circuit equivalent to the acoustical system shown in Figure 6.">Figure 8</a>.
   <figure class=figure>
<p><img src=${config.ASSETS_URL}/04/05/theory/figure-8.svg alt="Figure 8.Electrical circuit equivalent to the acoustical system shown in Figure 6."><figcaption class=caption>Figure 8.Electrical circuit equivalent to the acoustical system shown in <a href="#" data-img-src=${config.ASSETS_URL}/04/05/theory/figure-6.svg data-img-alt="Figure 6. Acoustical network."> Figure 6</a>.</figcaption></figure>
<h3 class=sectionHead>Conclusion</h3>
<p>In this Lesson we have seen how to draw the electrical circuits equivalent to mechanical or acoustic systems. This knowledge will be particularly useful when modelling complete electroacoustic systems [1, 2, 3], where a single electrical circuit can be drawn including elements of mechanical origin (the diaphragm of a microphone or loudspeaker), acoustical origin (the box of a loudspeaker or the acoustical network at the back of a microphone) and of course electrical elements (the coil of a loudspeaker or the electrodes of an electrostatic microphone). But before doing this, we need to know the laws of passage from one domain to another, this will be presented in the following Lessons.
<h3 class=likesectionHead>Glossary</h3>
<p>
   <dl>
         <dt>1-DOF
         <dd>1 Degree Of Freedom
   </dl>
<h3 class=sectionHead>References</h3>
<dl class=thebibliography>
   <dt id=X0-Beranek2012 class=thebibliography>[1]
   <dd id=bib-1 class=thebibliography>
      <p>Leo Beranek and Tim Mellow. <span class=cmti-10>Acoustics: sound fields and transducers</span>. Academic Pres, 2012. <span class=cmcsc-10><span class=small-caps>isbn</span></span>: 9780123914866.
   <dt id=X0-Kleiner2013 class=thebibliography>[2]
   <dd id=bib-2 class=thebibliography>
      <p>Mendel Kleiner. <span class=cmti-10>Electroacoustics</span>. CRC Press, 2013. <span class=cmcsc-10><span class=small-caps>isbn</span></span>: 9780429073519.
   <dt id=X0-Rossi1988 class=thebibliography>[3]
   <dd id=bib-3 class=thebibliography>
      <p>Mario Rossi. <span class=cmti-10>Acoustics and Electroacoustics</span>. Artech House, 1988. <span class=cmcsc-10><span class=small-caps>isbn</span></span>: 978-0890062555.
</dl>`;

export default theory;
